import { useState, useEffect, useRef } from 'react';
import { sendEmailCode, verifyEmailCode, changePassword } from '../../api/authApi';

const inputStyle = {
  boxSizing: 'border-box',
  flex: 1,
  height: '52px',
  padding: '0 18px',
  background: '#FCFCFD',
  border: '1px solid #E9E9EC',
  borderRadius: '10px',
  fontFamily: 'Pretendard, sans-serif',
  fontWeight: 400,
  fontSize: '16px',
  letterSpacing: '-0.04em',
  color: '#090909',
  outline: 'none',
};

const EMAIL_ERROR_MSG = {
  NO_ACCOUNT: '가입되지 않은 이메일입니다.',
  COOLTIME: '30초 후 다시 시도해주세요.',
  DAILY_LIMIT: '오늘 인증 요청 한도(5회)를 초과했습니다.',
  SEND_FAILED: '메일 발송에 실패했습니다. 잠시 후 다시 시도해주세요.',
};

const CODE_ERROR_MSG = {
  WRONG_CODE: '인증번호가 일치하지 않습니다.',
  EXPIRED: '인증번호가 만료되었습니다. 다시 요청해주세요.',
  LOCKED: '시도 횟수를 초과했습니다. 잠시 후 다시 시도해주세요.',
  ALREADY_USED: '이미 사용된 인증번호입니다. 새로 요청해주세요.',
};

function ActionBtn({ onClick, disabled, active, loading, status, defaultLabel, successLabel }) {
  const isSuccess = status === 'success';
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        height: '52px', padding: '0 18px', borderRadius: '10px',
        border: `1px solid ${isSuccess ? '#22B473' : '#E9E9EC'}`,
        background: '#fff',
        color: isSuccess ? '#22B473' : (active ? '#090909' : '#A2A2A2'),
        fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '15px',
        letterSpacing: '-0.04em',
        cursor: (active && !disabled && !loading) ? 'pointer' : 'default',
        whiteSpace: 'nowrap', flexShrink: 0, transition: 'color 0.2s, border-color 0.2s',
      }}
    >
      {loading ? '처리 중...' : isSuccess ? successLabel : defaultLabel}
    </button>
  );
}

function EyeIcon({ show }) {
  return show ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#A2A2A2" strokeWidth="1.5"/>
      <circle cx="12" cy="12" r="3" stroke="#A2A2A2" strokeWidth="1.5"/>
    </svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="1" y1="1" x2="23" y2="23" stroke="#A2A2A2" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

export default function FindPasswordModal({ onClose }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  const [emailStatus, setEmailStatus] = useState('idle');
  const [codeStatus, setCodeStatus] = useState('idle');
  const [pwError, setPwError] = useState('');
  const [emailMsg, setEmailMsg] = useState('');
  const [codeMsg, setCodeMsg] = useState('');

  const [isLoadingEmail, setIsLoadingEmail] = useState(false);
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [timer, setTimer] = useState(0);
  const timerRef = useRef(null);

  function startTimer() {
    if (timerRef.current) clearInterval(timerRef.current);
    setTimer(180);
    timerRef.current = setInterval(() => {
      setTimer(prev => {
        if (prev <= 1) { clearInterval(timerRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  function formatTimer(s) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, '0')} 남음`;
  }

  async function handleSendCode() {
    if (!email.trim() || isLoadingEmail) return;
    setIsLoadingEmail(true);
    setEmailStatus('idle'); setEmailMsg('');
    setCodeStatus('idle'); setCodeMsg('');
    try {
      const res = await sendEmailCode(email.trim());
      if (res?.success === false) {
        setEmailStatus('error');
        setEmailMsg(EMAIL_ERROR_MSG[res.errorCode] || '입력하신 이메일을 다시 확인해주세요.');
      } else {
        setEmailStatus('success');
        setEmailMsg('');
        startTimer();
      }
    } catch {
      setEmailStatus('error');
      setEmailMsg('입력하신 이메일을 다시 확인해주세요.');
    } finally {
      setIsLoadingEmail(false);
    }
  }

  async function handleVerifyCode() {
    if (!code.trim() || isLoadingCode || emailStatus !== 'success') return;
    setIsLoadingCode(true);
    setCodeStatus('idle'); setCodeMsg('');
    try {
      const res = await verifyEmailCode(email.trim(), code.trim());
      if (!res.verified) {
        setCodeStatus('error');
        setCodeMsg(CODE_ERROR_MSG[res.errorCode] || '인증번호가 일치하지 않습니다.');
      } else {
        setCodeStatus('success');
        setCodeMsg('');
        if (timerRef.current) clearInterval(timerRef.current);
        setTimer(0);
      }
    } catch {
      setCodeStatus('error');
      setCodeMsg('인증 요청 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setIsLoadingCode(false);
    }
  }

  async function handleSave() {
    if (codeStatus !== 'success') return;
    if (newPassword.length < 8) { setPwError('비밀번호는 8자 이상이어야 합니다.'); return; }
    if (newPassword !== confirmPassword) { setPwError('비밀번호가 일치하지 않습니다.'); return; }
    setPwError('');
    setIsSaving(true);
    try {
      await changePassword(email.trim(), newPassword);
      alert('비밀번호가 변경되었습니다.');
      onClose();
    } catch {
      setPwError('비밀번호 변경에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSaving(false);
    }
  }

  const canSave = codeStatus === 'success' && newPassword.length >= 8 && newPassword === confirmPassword && !isSaving;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.55)' }}
      onClick={onClose}
    >
      <div
        style={{ boxSizing: 'border-box', width: '520px', background: '#fff', borderRadius: '20px', padding: '40px 36px', display: 'flex', flexDirection: 'column', gap: '24px', position: 'relative' }}
        onClick={e => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 700, fontSize: '28px', letterSpacing: '-0.04em', color: '#090909' }}>비밀번호 찾기</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="#A2A2A2" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* 이메일 입력 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '16px', letterSpacing: '-0.04em', color: '#090909' }}>이메일 입력</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="email"
              placeholder="이메일 입력해주세요"
              value={email}
              onChange={e => { setEmail(e.target.value); setEmailStatus('idle'); setEmailMsg(''); }}
              disabled={emailStatus === 'success'}
              style={{ ...inputStyle, background: emailStatus === 'success' ? '#F8F8F8' : '#FCFCFD' }}
            />
            <ActionBtn
              onClick={handleSendCode}
              disabled={!email.trim()}
              active={!!email.trim() && emailStatus !== 'success'}
              loading={isLoadingEmail}
              status={emailStatus}
              defaultLabel="인증요청"
              successLabel="전송 완료"
            />
          </div>
          {emailMsg && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#FF4343" strokeWidth="1.5"/><path d="M15 9l-6 6M9 9l6 6" stroke="#FF4343" strokeWidth="1.5" strokeLinecap="round"/></svg>
              <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: '#FF4343', letterSpacing: '-0.04em' }}>{emailMsg}</span>
            </div>
          )}
        </div>

        {/* 인증번호 입력 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '16px', letterSpacing: '-0.04em', color: '#090909' }}>인증번호 입력</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              placeholder="전송된 메일을 확인해주세요"
              value={code}
              onChange={e => { setCode(e.target.value); setCodeStatus('idle'); setCodeMsg(''); }}
              disabled={codeStatus === 'success'}
              style={{ ...inputStyle, background: codeStatus === 'success' ? '#F8F8F8' : '#FCFCFD' }}
            />
            <ActionBtn
              onClick={handleVerifyCode}
              disabled={!code.trim() || emailStatus !== 'success'}
              active={!!code.trim() && emailStatus === 'success' && codeStatus !== 'success'}
              loading={isLoadingCode}
              status={codeStatus}
              defaultLabel="인증확인"
              successLabel="인증 완료"
            />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {codeMsg ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#FF4343" strokeWidth="1.5"/><path d="M15 9l-6 6M9 9l6 6" stroke="#FF4343" strokeWidth="1.5" strokeLinecap="round"/></svg>
                <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: '#FF4343', letterSpacing: '-0.04em' }}>{codeMsg}</span>
              </div>
            ) : <span />}
            {timer > 0 && codeStatus !== 'success' && (
              <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: '#A2A2A2', letterSpacing: '-0.04em' }}>{formatTimer(timer)}</span>
            )}
          </div>
        </div>

        {/* 구분선 */}
        <div style={{ borderTop: '1px solid #EEEEEE' }} />

        {/* 새로운 비밀번호 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '16px', letterSpacing: '-0.04em', color: '#090909' }}>새로운 비밀번호 설정</span>
          <div style={{ position: 'relative' }}>
            <input
              type={showNewPw ? 'text' : 'password'}
              placeholder="8자 이상 영문 입력"
              value={newPassword}
              onChange={e => { setNewPassword(e.target.value); setPwError(''); }}
              disabled={codeStatus !== 'success'}
              style={{ ...inputStyle, width: '100%', paddingRight: '46px', background: codeStatus !== 'success' ? '#F8F8F8' : '#FCFCFD' }}
            />
            <button onClick={() => setShowNewPw(v => !v)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
              <EyeIcon show={showNewPw} />
            </button>
          </div>
        </div>

        {/* 비밀번호 확인 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '16px', letterSpacing: '-0.04em', color: '#090909' }}>비밀번호 확인</span>
          <div style={{ position: 'relative' }}>
            <input
              type={showConfirmPw ? 'text' : 'password'}
              placeholder="8자 이상 영문 입력"
              value={confirmPassword}
              onChange={e => { setConfirmPassword(e.target.value); setPwError(''); }}
              disabled={codeStatus !== 'success'}
              style={{ ...inputStyle, width: '100%', paddingRight: '46px', background: codeStatus !== 'success' ? '#F8F8F8' : '#FCFCFD' }}
            />
            <button onClick={() => setShowConfirmPw(v => !v)} style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex' }}>
              <EyeIcon show={showConfirmPw} />
            </button>
          </div>
          {pwError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#FF4343" strokeWidth="1.5"/><path d="M15 9l-6 6M9 9l6 6" stroke="#FF4343" strokeWidth="1.5" strokeLinecap="round"/></svg>
              <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: '#FF4343', letterSpacing: '-0.04em' }}>{pwError}</span>
            </div>
          )}
        </div>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: '8px', paddingTop: '4px' }}>
          <button
            onClick={onClose}
            style={{ flex: 1, height: '52px', borderRadius: '10px', border: '1px solid #E9E9EC', background: '#fff', fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '16px', letterSpacing: '-0.04em', color: '#A2A2A2', cursor: 'pointer' }}
          >
            취소
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            style={{ flex: 1, height: '52px', borderRadius: '10px', border: 'none', background: canSave ? '#090909' : '#E9E9EC', fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '16px', letterSpacing: '-0.04em', color: canSave ? '#fff' : '#A2A2A2', cursor: canSave ? 'pointer' : 'default', transition: 'background 0.2s' }}
          >
            {isSaving ? '변경 중...' : '완료'}
          </button>
        </div>
      </div>
    </div>
  );
}
